import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { db } from "../services/firebase"; // Asumiendo que db está correctamente configurado

// --- INICIO DE FUNCIÓN AUXILIAR ROBUSTA ---
const getRobustValue = (dataObject, targetKeyName) => {
  if (dataObject && typeof dataObject === "object") {
    const normalizedTargetKey = targetKeyName.trim().toLowerCase();
    for (const currentKey in dataObject) {
      if (Object.prototype.hasOwnProperty.call(dataObject, currentKey)) {
        const normalizedCurrentKey = currentKey.trim().toLowerCase();
        if (normalizedCurrentKey === normalizedTargetKey) {
          return { value: dataObject[currentKey], foundKey: currentKey };
        }
      }
    }
  }
  // En producción, podrías decidir si este warn es necesario o si prefieres un logging más silencioso
  // console.warn(`[SubscriptionProvider - getRobustValue] Key "${targetKeyName}" not found or dataObject is not a valid object.`);
  return { value: undefined, foundKey: null };
};
// --- FIN DE FUNCIÓN AUXILIAR ROBUSTA ---

const SubscriptionContext = createContext(null); // Inicializar con null es común

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined || context === null) {
    // Comprobar también undefined si es posible
    throw new Error(
      "useSubscription debe usarse dentro de un SubscriptionProvider y el contexto no debe ser undefined/null",
    );
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isUserActuallySubscribed, setIsUserActuallySubscribed] =
    useState(false);
  const [loading, setLoading] = useState(true); // Iniciar como true es correcto
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const authInstance = getAuth(); // Renombrado para evitar conflicto con la variable 'auth' importada si la tuvieras
    const unsubscribeAuth = onAuthStateChanged(authInstance, (user) => {
      setCurrentUser(user);
      if (!user) {
        // Resetear estados si el usuario cierra sesión
        setSubscriptionDetails(null);
        setIsUserActuallySubscribed(false);
        setIsAdmin(false);
        setLoading(false); // Termina la carga si no hay usuario
      }
      // Si hay usuario, el otro useEffect se encargará de setLoading(true) y luego false
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      // Asegurar que si currentUser se vuelve null (por ej. logout), los estados se resetean
      // y no se intente una lectura de Firestore.
      // El useEffect de auth ya maneja esto, pero una doble verificación no daña.
      setSubscriptionDetails(null);
      setIsUserActuallySubscribed(false);
      setIsAdmin(false);
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    setError(null); // Limpiar errores anteriores al reintentar
    const userDocRef = doc(db, "users", currentUser.uid);

    const unsubscribeSnapshot = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();

          const adminCheckRole = getRobustValue(userData, "role");
          const adminCheckIsAdmin = getRobustValue(userData, "isAdmin");
          const adminCheckRolesArray = getRobustValue(userData, "roles");
          const determinedIsAdmin =
            adminCheckIsAdmin.value === true ||
            adminCheckRole.value === "admin" ||
            (Array.isArray(adminCheckRolesArray.value) &&
              adminCheckRolesArray.value.includes("admin"));
          setIsAdmin(determinedIsAdmin);

          const { value: statusFromUserData } = getRobustValue(
            userData,
            "subscriptionStatus",
          );
          const determinedIsActive = statusFromUserData === "active";
          setIsUserActuallySubscribed(determinedIsActive);

          if (determinedIsActive) {
            const planIdResult = getRobustValue(userData, "subscriptionPlan");
            const tierResult = getRobustValue(userData, "subscriptionTier");
            const startDateResult = getRobustValue(
              userData,
              "subscriptionStart",
            );
            const endDateResult = getRobustValue(userData, "subscriptionEnd");

            setSubscriptionDetails({
              status: statusFromUserData,
              planId: planIdResult.value || "unknown",
              tier: tierResult.value || "free",
              startDate: startDateResult.value?.toDate
                ? startDateResult.value.toDate()
                : null,
              endDate: endDateResult.value?.toDate
                ? endDateResult.value.toDate()
                : null,
            });
          } else {
            setSubscriptionDetails(null);
          }
        } else {
          // El documento del usuario no existe, tratar como no suscrito y no admin
          console.warn(
            `[SubscriptionProvider] User document does not exist for UID: ${currentUser.uid}. Treating as non-subscribed/non-admin.`,
          );
          setIsAdmin(false);
          setIsUserActuallySubscribed(false);
          setSubscriptionDetails(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error(
          "[SubscriptionProvider] Error in Firestore onSnapshot listener:",
          err,
        );
        setError(
          "Error al cargar la información de tu suscripción. Inténtalo de nuevo más tarde.",
        ); // Mensaje más amigable para el usuario
        setIsAdmin(false);
        setIsUserActuallySubscribed(false);
        setSubscriptionDetails(null);
        setLoading(false);
      },
    );

    return () => unsubscribeSnapshot();
  }, [currentUser]); // Dependencia correcta

  const subscribe = useCallback(
    async (planId) => {
      if (!currentUser) {
        // Este error se lanzará y debería ser capturado por quien llame a subscribe
        throw new Error("Debe iniciar sesión para suscribirse.");
      }
      setLoading(true);
      setError(null);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch("/api/subscription-session", {
          // Asegúrate que esta URL sea configurable o correcta para producción
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ planId }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(
            data.error || "Hubo un problema al procesar tu suscripción.",
          );
        }
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else if (data.sessionId) {
          // Log útil si usas Stripe Elements y necesitas el sessionId
          console.log(
            "[SubscriptionProvider] Stripe Elements sessionId received:",
            data.sessionId,
          );
        } else {
          // Esperando webhook, la UI debería manejar este estado
          console.log(
            "[SubscriptionProvider] Suscripción iniciada, esperando actualización del webhook.",
          );
        }
        // No es necesario setLoading(false) aquí si hay redirección o si el webhook actualiza el estado
        return data;
      } catch (err) {
        console.error(
          "[SubscriptionProvider] Error en la función subscribe:",
          err,
        );
        setError(
          err.message ||
            "No se pudo procesar tu suscripción. Por favor, inténtalo de nuevo.",
        );
        setLoading(false); // Importante resetear loading en caso de error
        throw err; // Re-lanzar para que el llamador pueda manejarlo
      }
    },
    [currentUser],
  );

  const cancelSubscription = useCallback(async () => {
    if (!currentUser) {
      throw new Error("Debe iniciar sesión para cancelar una suscripción.");
    }
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch("/api/subscription/cancel", {
        // Asegúrate que esta URL sea configurable o correcta para producción
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.error || "Hubo un problema al cancelar tu suscripción.",
        );
      }
      // La UI debería reaccionar al cambio de estado que provocará el webhook
      console.log(
        "[SubscriptionProvider] Solicitud de cancelación de suscripción enviada.",
      );
      // setLoading(false) se manejará por el cambio de estado del webhook
      return data;
    } catch (err) {
      console.error(
        "[SubscriptionProvider] Error en la función cancelSubscription:",
        err,
      );
      setError(
        err.message ||
          "No se pudo cancelar tu suscripción. Por favor, inténtalo de nuevo.",
      );
      setLoading(false);
      throw err;
    }
  }, [currentUser]);

  const hasFeatureAccess = useCallback(
    (featureId) => {
      // Esta lógica es específica de tu aplicación y parece correcta
      if (isAdmin) return true;
      const featuresByPlan = {
        free: ["basic_content", "forum_read"],
        basic: [
          "basic_content",
          "forum_read",
          "forum_write",
          "resources_basic",
        ],
        premium: [
          "basic_content",
          "forum_read",
          "forum_write",
          "resources_basic",
          "resources_premium",
          "support_priority",
        ],
        // Podrías añadir un plan 'unknown' o manejarlo explícitamente
      };
      if (
        !isUserActuallySubscribed ||
        !subscriptionDetails ||
        !subscriptionDetails.planId
      ) {
        return featuresByPlan.free.includes(featureId);
      }
      const planFeatures =
        featuresByPlan[subscriptionDetails.planId] || featuresByPlan.free;
      return planFeatures.includes(featureId);
    },
    [isAdmin, isUserActuallySubscribed, subscriptionDetails],
  );

  const getRemainingDays = useCallback(() => {
    if (
      !isUserActuallySubscribed ||
      !subscriptionDetails ||
      !subscriptionDetails.endDate
    ) {
      return 0;
    }
    const today = new Date();
    const end = subscriptionDetails.endDate; // Asumiendo que es un objeto Date
    if (!(end instanceof Date) || isNaN(end.getTime())) {
      console.warn(
        "[SubscriptionProvider] subscriptionDetails.endDate no es una fecha válida.",
      );
      return 0;
    }
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, [isUserActuallySubscribed, subscriptionDetails]);

  const value = {
    subscriptionDetails,
    loading,
    error,
    subscribe,
    cancelSubscription,
    hasFeatureAccess,
    getRemainingDays,
    isSubscribed: isUserActuallySubscribed || isAdmin, // Lógica de negocio
    isAdmin,
    currentUser, // Exponer currentUser puede ser útil para algunos componentes
  };

  // Eliminar el LOG 9 masivo en producción
  // console.log('[LOG 9 - SubscriptionProvider] Context value being provided:', value);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
